// Creative workspace: current version workflow, (future) editor and preview
// placeholders, versions and distributions. Hierarchy context is shown as a
// quiet breadcrumb, not as navigation the user must understand first.
import type { ColumnDef } from "@tanstack/react-table";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Eye, FilePlus2, Pencil, Wand2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { api, call } from "@/api/client";
import { keys, useFrontDesk } from "@/api/hooks";
import type { CreativeVersionSummary, Distribution } from "@/api/types";
import { formatDateTime } from "@/lib/format";
import { PRODUCTS, displayFormat } from "@/lib/labels";
import { Button } from "@/ui/button";
import { Callout, EmptyState, PageHeader, Panel, Section } from "@/ui/layout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/ui/tabs";
import { DataTable } from "@/components/data-table";
import { NameDialog } from "@/components/name-dialog";
import { ServingStateBadge, VersionStateBadge } from "@/components/status";
import { Breadcrumbs } from "../common/breadcrumbs";
import { notifyError } from "../common/notify";
import { PageError, PageLoading, useTitle, useRouteParams, useSearchRecord } from "../common/page";
import { useCreative, useCreativeDistributions, useVersions } from "../common/queries";
import { DistributionDialog } from "../distributions/distribution-dialog";
import { CompatibilityBadge } from "../platforms/compatibility-summary";
import { VersionPanel } from "./version-panel";

export function CreativeDetailPage() {
  const { orgId, creativeId } = useRouteParams("orgId", "creativeId");
  const search = useSearchRecord();
  const queryClient = useQueryClient();
  const { data: fd } = useFrontDesk(orgId);
  const creative = useCreative(orgId, creativeId);
  const versions = useVersions(orgId, creativeId);
  const distributions = useCreativeDistributions(orgId, creativeId);
  const [renameOpen, setRenameOpen] = useState(false);
  const [distributionOpen, setDistributionOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  useTitle(creative.data?.name);

  if (creative.isLoading) return <PageLoading />;
  if (creative.error || !creative.data) return <PageError error={creative.error} orgId={orgId} />;
  const c = creative.data;
  const base = `/o/${orgId}`;
  const latest = versions.items[0];
  const published = versions.items.find((v) => v.state === "PUBLISHED");
  const canEdit = fd?.capabilities.createCreative ?? false;
  const canDistribute = fd?.capabilities.manageDistributions ?? false;

  const newVersion = async (fromVersionId?: string) => {
    setCreating(true);
    try {
      const v = await call(() =>
        api.POST("/orgs/{orgId}/creatives/{creativeId}/versions", {
          params: { path: { orgId, creativeId } },
          body: fromVersionId ? { fromVersionId } : {},
        })
      );
      toast.success(`Versión ${String(v.versionNumber)} creada en borrador`);
      await queryClient.invalidateQueries({ queryKey: keys.org(orgId) });
    } catch (problem) {
      notifyError(problem);
    } finally {
      setCreating(false);
    }
  };

  const versionColumns: Array<ColumnDef<CreativeVersionSummary>> = [
    {
      header: "Versión",
      cell: ({ row }) => (
        <Link to={`${base}/versions/${row.original.id}`} className="font-medium hover:underline">
          Versión {row.original.versionNumber}
        </Link>
      ),
    },
    { header: "Estado", cell: ({ row }) => <VersionStateBadge state={row.original.state} /> },
    { header: "Creada", cell: ({ row }) => formatDateTime(row.original.createdAt) },
    { header: "Publicada", cell: ({ row }) => formatDateTime(row.original.publishedAt) },
    {
      header: "Activa en",
      cell: ({ row }) =>
        row.original.activeDistributions > 0 ? `${String(row.original.activeDistributions)} distribución(es)` : "—",
    },
  ];
  const distributionColumns: Array<ColumnDef<Distribution>> = [
    {
      header: "Distribución",
      cell: ({ row }) => (
        <Link to={`${base}/distributions/${row.original.id}`} className="font-medium hover:underline">
          {row.original.name}
        </Link>
      ),
    },
    {
      header: "Dónde se usa",
      cell: ({ row }) => (
        <span className="grid">
          <span>
            {row.original.platform.name} · {displayFormat(row.original.format)}
            {row.original.platform.kind === "custom" ? (
              <span className="text-xs text-muted"> · personalizada</span>
            ) : null}
          </span>
          <CompatibilityBadge status={row.original.compatibility.overall} />
        </span>
      ),
    },
    { header: "Estado", cell: ({ row }) => <ServingStateBadge state={row.original.serving.effectiveState} /> },
    {
      header: "Versión activa",
      cell: ({ row }) =>
        row.original.activeVersion ? `Versión ${String(row.original.activeVersion.versionNumber)}` : "—",
    },
  ];

  return (
    <div className="grid gap-6">
      <Breadcrumbs orgId={orgId} campaignId={c.campaignId} brandId={c.brandId} clientId={c.clientId} current={c.name} />
      <PageHeader
        eyebrow={PRODUCTS[c.product].label}
        title={c.name}
        actions={
          canEdit ? (
            <>
              <Button
                size="sm"
                onClick={() => {
                  setRenameOpen(true);
                }}
              >
                <Pencil /> Renombrar
              </Button>
              {latest && latest.state !== "DRAFT" ? (
                <Button size="sm" disabled={creating} onClick={() => void newVersion(latest.id)}>
                  <FilePlus2 /> Nueva versión
                </Button>
              ) : null}
            </>
          ) : null
        }
      />
      {search.created ? (
        <Callout tone="success" title="Tu creativo está listo">
          Siguiente: diséñalo en el Studio y después envíalo a revisión, apruébalo y publícalo para obtener tu Tag
          permanente.
        </Callout>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-[1fr_20rem]">
        <Section title={latest ? `Versión ${String(latest.versionNumber)}` : "Versión"}>
          <Panel className="grid gap-4 p-4">
            {latest ? (
              <VersionPanel
                orgId={orgId}
                version={latest}
                distributions={distributions.items}
                canCreateDistribution={canDistribute}
                product={c.product}
              />
            ) : versions.isLoading ? null : (
              <EmptyState
                title="Este creativo aún no tiene versiones"
                action={
                  canEdit ? (
                    <Button variant="primary" disabled={creating} onClick={() => void newVersion()}>
                      Crear primera versión
                    </Button>
                  ) : null
                }
              />
            )}
          </Panel>
        </Section>
        <div className="grid content-start gap-3">
          <Panel className="grid gap-2 p-4">
            <span className="flex items-center gap-2 text-sm font-medium">
              <Wand2 className="size-4 text-brand" aria-hidden="true" /> Creative Studio
            </span>
            <p className="text-sm text-muted">
              {latest?.editable
                ? `Diseña la versión ${String(latest.versionNumber)}: textos, imágenes, formas, botones y varios formatos.`
                : latest
                  ? `La versión ${String(latest.versionNumber)} ya no está en borrador: puedes verla en el Studio. Para cambiarla, crea una nueva versión.`
                  : "Crea una versión para empezar a diseñar."}
            </p>
            {latest ? (
              <Button
                asChild
                variant={latest.editable ? "primary" : "secondary"}
                size="sm"
                className="justify-self-start"
              >
                <Link to={`${base}/versions/${latest.id}/studio`}>
                  {latest.editable ? <Pencil /> : <Eye />} {latest.editable ? "Abrir en el Studio" : "Ver en el Studio"}
                </Link>
              </Button>
            ) : null}
          </Panel>
        </div>
      </div>

      <Tabs defaultValue="distributions">
        <TabsList>
          <TabsTrigger value="distributions">Distribuciones y Tags</TabsTrigger>
          <TabsTrigger value="versions">Versiones</TabsTrigger>
        </TabsList>
        <TabsContent value="distributions" className="grid gap-3">
          {canDistribute ? (
            <div>
              <Button
                size="sm"
                onClick={() => {
                  setDistributionOpen(true);
                }}
              >
                Nueva distribución
              </Button>
            </div>
          ) : null}
          <DataTable
            label="Distribuciones"
            columns={distributionColumns}
            rows={distributions.items}
            loading={distributions.isLoading}
            hasMore={distributions.hasNextPage}
            loadingMore={distributions.isFetchingNextPage}
            onLoadMore={() => void distributions.fetchNextPage()}
            empty={
              <EmptyState
                title="Aún no hay distribuciones"
                description="Una distribución define dónde vas a utilizar el creativo (plataforma y formato) y le da su Tag permanente. Un mismo creativo puede tener varias."
              />
            }
          />
        </TabsContent>
        <TabsContent value="versions">
          <DataTable
            label="Versiones"
            columns={versionColumns}
            rows={versions.items}
            loading={versions.isLoading}
            hasMore={versions.hasNextPage}
            loadingMore={versions.isFetchingNextPage}
            onLoadMore={() => void versions.fetchNextPage()}
            empty={<EmptyState title="Sin versiones" />}
          />
        </TabsContent>
      </Tabs>

      <NameDialog
        open={renameOpen}
        onOpenChange={setRenameOpen}
        title="Renombrar creativo"
        label="Nombre"
        initial={c.name}
        submitLabel="Guardar"
        onSubmit={async (name) => {
          await call(() =>
            api.PATCH("/orgs/{orgId}/creatives/{creativeId}", {
              params: { path: { orgId, creativeId } },
              body: { name },
            })
          );
          await queryClient.invalidateQueries({ queryKey: keys.org(orgId) });
        }}
      />
      <DistributionDialog
        key={String(distributionOpen)}
        orgId={orgId}
        creativeId={creativeId}
        product={c.product}
        formatsFromVersionId={latest?.id}
        publishedVersion={published ? { id: published.id, versionNumber: published.versionNumber } : undefined}
        canAddPlatform={canDistribute}
        open={distributionOpen}
        onOpenChange={setDistributionOpen}
      />
    </div>
  );
}
